import { Button, Input, Tab, Tabs } from '@heroui/react';
import { Check, Copy, Download, Redo2, Save, Send, Undo2, X } from 'lucide-react';
import React, { FC, useState } from 'react';

import type { EmailDocument } from '../model/types';
import type { PreviewDevice } from './EmailPreview';

interface BuilderTopBarProps {
  doc: EmailDocument;
  device: PreviewDevice;
  canUndo: boolean;
  canRedo: boolean;
  onDevice: (device: PreviewDevice) => void;
  onMeta: (patch: Partial<Pick<EmailDocument, 'name' | 'subject' | 'preheader'>>) => void;
  onUndo: () => void;
  onRedo: () => void;
  onClose: () => void;
  onCopyHtml: () => Promise<void>;
  onDownloadHtml: () => void;
  onSave: () => void;
  onSend: () => void;
  isSaving: boolean;
  /** Ustawione, gdy szablon ma już wpis w bazie: przycisk nadpisuje, a nie zakłada nowy. */
  savedId: string | null;
}

export const BuilderTopBar: FC<BuilderTopBarProps> = ({
  doc, device, canUndo, canRedo, onDevice, onMeta, onUndo, onRedo, onClose, onCopyHtml, onDownloadHtml,
  onSave, onSend, isSaving, savedId,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await onCopyHtml();
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-wrap items-end gap-4 border-b border-gray-200 bg-white px-4 py-3">
      {/* Trzy pola w siatce o równych kolumnach i z etykietą NAD polem. Przy etykiecie
          pływającej i podpowiedzi pod jednym z nich każde miało inną wysokość i pasek
          się rozjeżdżał. */}
      <div className="grid min-w-[520px] flex-1 grid-cols-3 gap-3">
        <Input
          size="sm"
          labelPlacement="outside"
          label="Nazwa szablonu"
          placeholder="Widoczna tylko w panelu"
          value={doc.name}
          onValueChange={(name) => onMeta({ name })}
        />
        <Input
          size="sm"
          labelPlacement="outside"
          label="Temat"
          placeholder="Temat wiadomości"
          value={doc.subject}
          onValueChange={(subject) => onMeta({ subject })}
        />
        <Input
          size="sm"
          labelPlacement="outside"
          label="Zajawka"
          placeholder="Tekst obok tematu w skrzynce"
          value={doc.preheader}
          onValueChange={(preheader) => onMeta({ preheader })}
        />
      </div>

      <div className="ml-auto flex items-center gap-2">
        <Tabs
          size="sm"
          aria-label="Podgląd"
          selectedKey={device}
          onSelectionChange={(key) => onDevice(key as PreviewDevice)}
        >
          <Tab key="desktop" title="Komputer" />
          <Tab key="mobile" title="Telefon" />
        </Tabs>

        <Button isIconOnly size="sm" variant="light" aria-label="Cofnij" isDisabled={!canUndo} onPress={onUndo}>
          <Undo2 className="h-4 w-4" />
        </Button>
        <Button isIconOnly size="sm" variant="light" aria-label="Ponów" isDisabled={!canRedo} onPress={onRedo}>
          <Redo2 className="h-4 w-4" />
        </Button>

        <Button
          size="sm"
          variant="bordered"
          startContent={copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          onPress={handleCopy}
        >
          {copied ? 'Skopiowano' : 'Kopiuj HTML'}
        </Button>
        <Button size="sm" variant="bordered" startContent={<Download className="h-4 w-4" />} onPress={onDownloadHtml}>
          Pobierz
        </Button>
        <Button
          size="sm"
          variant="bordered"
          isLoading={isSaving}
          startContent={<Save className="h-4 w-4" />}
          onPress={onSave}
        >
          {savedId ? 'Zapisz zmiany' : 'Zapisz szablon'}
        </Button>
        <Button size="sm" color="primary" startContent={<Send className="h-4 w-4" />} onPress={onSend}>
          Wyślij
        </Button>
        <Button isIconOnly size="sm" variant="light" aria-label="Zamknij kreator" onPress={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
